import * as IssueDomain from "@/domain/issue"
import { escapeMarkdown } from "@/helpers/markdown.helper"
import { getImageNameWithTag } from "@/helpers/string.helpers"
import { SourceItem } from "@/organisms/Drawers/components/SourceList/SourceList.types"
import { findingsToSources } from "@/organisms/Drawers/components/SourceList/utils"
import { findingsToBaseImageSources } from "@/organisms/Drawers/components/TriageDrawer/components/PackageVulnerabilityCards/components/RemediationCard/components/Fixes/BaseImageFixes/utils"
import { getUniqueImagePackageFixes } from "@/organisms/Drawers/components/TriageDrawer/components/PackageVulnerabilityCards/components/RemediationCard/components/Fixes/PackageUpdates"
import { findingsToThirdPartySources } from "@/organisms/Drawers/components/TriageDrawer/components/PackageVulnerabilityCards/components/RemediationCard/components/Fixes/ThirdPartyFixes"

import type { Fix } from "@/api/useTriage.types"
import type {
  ApplicativeImageFinding,
  BaseImageFinding,
  BaseImageFix,
  CodeRepoFinding,
  ExposedSecretFinding,
  InternetFacing,
  Issue,
  ThirdPartyFinding,
  ThirdPartyFix,
  VulnerableCodeFinding
} from "@/domain/issue"
import type { ExploitMaturity } from "@/domain/package/package.types"
import type { TFunction } from "@/hooks/useTranslation"

interface Attr {
  t: TFunction
  linkToDrawer?: string
}

export const getIssueHTML = (
  fixes: Fix[],
  attr: { t: TFunction; linkToDrawer?: string }
) => {
  const summary = getSummary(fixes)
  const description = fixes.map((fix) => getFixHTML(fix, attr)).join("\n")

  return { description, summary }
}

export const getSummary = (fixes: Fix[]) => {
  const hasFix = !!fixes?.length

  if (!hasFix) return ""

  // currently we only support in one issue:
  const { cwe, riskId } = fixes[0].issues[0]
  const issueName = cwe?.description ? cwe.description : riskId

  return `Fix '${issueName}' issue in project ${fixes[0].projectName}`
}

const internetFacingConversion: Record<InternetFacing, string> = {
  YES: "Yes",
  NO: "No",
  UNDETERMINED: "Undetermined"
}

const exploitMaturityConversion: Record<ExploitMaturity, string> = {
  POC: "Proof of concept",
  IN_THE_WILD: "In the wild",
  UNDETERMINED: "Undetermined"
}

export const getIssueImpact = (issue: Issue) => {
  const internetFacing = IssueDomain.isInternetFacingDetermined(issue)
    ? ` || internet facing: ${internetFacingConversion[IssueDomain.getInternetFacing(issue)]}`
    : ""
  const ingress = IssueDomain.isIngressTrue(issue)
    ? " || ingress traffic: Yes"
    : ""
  const runtime = IssueDomain.isRuntime(issue)
    ? " || package used in runtime: Yes"
    : ""
  const executed = IssueDomain.isExecuted(issue)
    ? " || function executed: Yes"
    : ""
  const exploit = IssueDomain.isExploitMaturityDetermined(issue)
    ? ` || known exploit: ${exploitMaturityConversion[IssueDomain.getExploitMaturity(issue)]}`
    : ""
  const applicationsCount = IssueDomain.getRelatedApplicationsCount(issue)

  return `  * Security impact: impacted applications: ${applicationsCount}${internetFacing}${ingress}${runtime}${executed}${exploit}`
}

export const getIssueInfo = (issue: Issue, linkToDrawer?: string) => {
  const titleText = getIssueTitle(issue)
  const title = linkToDrawer ? `[${titleText}](${linkToDrawer})` : titleText
  const issueImpact = getIssueImpact(issue)

  return `###${issue.severity} Severity Issue \n ${title}\n\n ${issueImpact}\n\n`
}

export const getFixVersions = (issue: Issue) => {
  if (issue.fixVersions.length !== 0) {
    const fixVersions = `\`\`\`${issue.packageName} ${issue.fixVersions.join(
      ", "
    )}\`\`\``
    return `**The issue is fixed in package version:** ${fixVersions} \n\n`
  }
  return ""
}

const getSourceFixes = (source: SourceItem, t: TFunction): string => {
  const { remedies, computationError } = source
  const tKey = "inventory.images.drawer.issue."
  let acc = ""

  const hasCodeRepoFixes = remedies.length > 0

  if (hasCodeRepoFixes) {
    acc = remedies.reduce((result, fix) => {
      const name = escapeMarkdown(fix.name)

      if (fix.reinstall) {
        result += `    * ${t("general.reinstall")}: ${name}:${fix.version}\n`
      } else {
        const version = `\`\`\`${fix.version}\`\`\``
        const knownFixType =
          fix.fixType === "patch" ||
          fix.fixType === "minor" ||
          fix.fixType === "major"
        const fixTypeText = knownFixType
          ? `${t(`general.${fix.fixType}Update`)}: `
          : ""
        const currentVersion = fix.currentVersion
          ? `:${fix.currentVersion}`
          : ""

        result += `    * ${fixTypeText}${name}${currentVersion} -> ${version}\n`
      }

      return result
    }, "")
  }

  if (!hasCodeRepoFixes) {
    acc += `    * _${t(`${tKey}kodemNoFix`)}_\n`
  }

  if (computationError) {
    acc += `    * _${t(`${tKey}${computationError}`)}_\n`
  }

  return acc
}

const getImageFixes = (
  imageFindings: ApplicativeImageFinding[],
  t: TFunction
): string => {
  let acc = ""
  const hasImageFixes = imageFindings?.some((f) => f.remedies?.length > 0)
  const uniqueFixes = getUniqueImagePackageFixes(imageFindings)

  if (hasImageFixes) {
    uniqueFixes.forEach((fix, index) => {
      const { packageName, fixType } = fix
      const title = t(`general.${fixType}Update`)
      acc += `${index + 1}. ${title} (${packageName})\n`
    })
  }

  return acc
}

export const getCodeRepoPackageUpdates = (
  codeRepoPkgFindings: CodeRepoFinding[],
  t: TFunction
): string => {
  const hasCodeRepoFindingsWithFixes = codeRepoPkgFindings?.some(
    (f: any) => f.remedies?.length > 0
  )
  const hasCodeRepoFindingsWithErrors = codeRepoPkgFindings?.some(
    (f: any) => f.computationError?.length > 0
  )

  if (!hasCodeRepoFindingsWithFixes && !hasCodeRepoFindingsWithErrors) return ""

  const sources: SourceItem[] = findingsToSources(codeRepoPkgFindings)
  const updates = sources
    .map((source, i) => {
      return `\n\n ${i + 1}. Source: ${escapeMarkdown(source.name)}\n\n${getSourceFixes(
        source,
        t
      )}`
    })
    .join("\n")
  return updates
}

export const getMaliciousPackageUpdate = (
  codeRepoPkgFindings: any,
  t: TFunction
): string => {
  //code repo finding can be only one for one issue
  const { remedies, computationError } = codeRepoPkgFindings[0]
  const tKey = "inventory.images.drawer.issue."
  let acc = ``

  if (remedies.length === 1) {
    //code repo finding can has only one fix for malicouse pkg
    const fix = remedies[0]
    acc += `${escapeMarkdown(fix.name || fix.packageName)} (${t(
      "inventory.codeRepositoriesTab.drawer.issue.nestedRow.remove"
    )})`
    if (computationError) {
      acc += `    * _${t(`${tKey}${computationError}`)}_\n`
    }
  }
  return acc
}

export const getPackageUpdates = (
  fix: Fix,
  issueType: string,
  t: TFunction
): string => {
  const { codeRepoPkgFindings, imagePkgFindings } = fix
  const codeRepoPackageUpdates = getCodeRepoPackageUpdates(
    codeRepoPkgFindings,
    t
  )

  const imagePackageUpdates = getImageFixes(fix.imagePkgFindings, t)

  if (issueType === "Malicious_Package") {
    const findings = codeRepoPkgFindings?.length
      ? codeRepoPkgFindings
      : imagePkgFindings
    return getMaliciousPackageUpdate(findings, t)
  }
  return codeRepoPackageUpdates.length > 0 || imagePackageUpdates.length > 0
    ? `\n\n**Update the following packages:**\n\n ${imagePackageUpdates}${codeRepoPackageUpdates}`
    : ``
}

const getBaseImageSourceFixes = (
  remedies: BaseImageFix[],
  t: TFunction
): string => {
  let acc = ""

  remedies.forEach((fix) => {
    const { fixType, tags, repository, currentUrl } = fix
    const isRebuild = fixType === "rebuild"
    const isReplaceOrRemove = fixType === "replace" || fixType === "remove"
    const title = isRebuild
      ? t("general.patchUpdate")
      : t(`general.${fixType}Update`)

    let value = ""
    if (isRebuild) {
      value = `\`\`\`${escapeMarkdown(repository)}:${tags?.[0] || ""}\`\`\``
    } else if (!isReplaceOrRemove) {
      value = `${escapeMarkdown(currentUrl || repository)} -> ${tags?.[0] ? `\`\`\`${tags[0]}\`\`\`` : ""}`
    }

    acc += `* ${title}${value ? `: ${value}` : ""}\n`
  })

  return acc
}

const getBaseImageFixes = (
  baseImageFindings: BaseImageFinding[],
  hasFixVersion: boolean,
  t: TFunction,
  tKey: string
): string => {
  if (baseImageFindings.length === 0) {
    return ""
  }
  let acc = ""
  const baseImageSources = findingsToBaseImageSources(baseImageFindings)
  const allBaseImageFixes: BaseImageFix[] = baseImageSources
    .map((f) => f.remedies)
    .flat()
  const hasFixes = allBaseImageFixes.length > 0
  const hasSingleBaseImages = baseImageSources.length === 1

  if (!hasFixes) {
    if (!hasFixVersion) {
      return ""
    }

    acc += `_${t(`${tKey}undeterminedBaseImageFix`)}_\n`
    acc += `_${t(`${tKey}undeterminedBaseImageFix2`)}_\n`
  } else if (hasSingleBaseImages) {
    acc += getBaseImageSourceFixes(baseImageSources[0].remedies, t)
  } else {
    acc += baseImageSources
      .map((source, i) => {
        return `\n\n ${i + 1}. ${escapeMarkdown(source.tag)}\n\n${getBaseImageSourceFixes(
          source.remedies,
          t
        )}`
      })
      .join("\n")
  }

  return `\n\n**Update the Base Image${hasSingleBaseImages ? "" : "s"}:**\n\n ${acc}`
}

const getThirdPartySourceFixes = (
  remedies: ThirdPartyFix[],
  t: TFunction
): string => {
  let acc = ""

  remedies.forEach((fix) => {
    const { fixType, tags, repository, currentUrl } = fix
    const isRebuild = fixType === "rebuild"
    const isReplaceOrRemove = fixType === "replace" || fixType === "remove"
    const title = isRebuild
      ? t("general.patchUpdate")
      : t(`general.${fixType}Update`)

    let value = ""
    if (isRebuild) {
      value = `\`\`\`${escapeMarkdown(repository)}:${tags?.[0] || ""}\`\`\``
    } else if (!isReplaceOrRemove) {
      value = `${escapeMarkdown(currentUrl || repository)} -> ${tags?.[0] ? `\`\`\`${tags[0]}\`\`\`` : ""}`
    }

    acc += `* ${title}${value ? `: ${value}` : ""}\n`
  })

  return acc
}

const getThirdPartyFixes = (
  thirdPartyFindings: ThirdPartyFinding[],
  hasFixVersion: boolean,
  t: TFunction,
  tKey: string
): string => {
  if (thirdPartyFindings.length === 0) {
    return ""
  }
  let acc = ""
  const thirdPartySources = findingsToThirdPartySources(thirdPartyFindings)
  const allThirdPartyFixes: ThirdPartyFix[] = thirdPartySources
    .map((f) => f.remedies)
    .flat()
  const hasFixes = allThirdPartyFixes.length > 0
  const hasSingleThirdPartyImages = thirdPartySources.length === 1

  if (!hasFixes) {
    if (!hasFixVersion) {
      return ""
    }

    acc += `_${t(`${tKey}undeterminedThirdPartyFix`)}_\n`
  } else if (hasSingleThirdPartyImages) {
    acc += getThirdPartySourceFixes(thirdPartySources[0].remedies, t)
  } else {
    acc += thirdPartySources
      .map((source, i) => {
        return `\n\n ${i + 1}. ${escapeMarkdown(source.tag)}\n\n${getThirdPartySourceFixes(
          source.remedies,
          t
        )}`
      })
      .join("\n")
  }

  return `\n\n**Update the Third Party Image${hasSingleThirdPartyImages ? "" : "s"}:**\n\n ${acc}`
}

const getImagesList = (fix: Fix): string => {
  let acc = ""
  const thirdPartyFindingsSet = new Set(fix.thirdPartyFindings || [])
  const allImagesFindings = [
    ...fix.imagePkgFindings,
    ...fix.baseImageFindings,
    ...(fix.thirdPartyFindings || [])
  ]

  const uniqueImages = Array.from(
    new Set(
      allImagesFindings.map((finding) =>
        getImageNameWithTag(finding.resource.name, finding.resource.tag)
      )
    )
  )
  if (uniqueImages.length === 0) return ""

  uniqueImages.forEach((image, index) => {
    acc += `${index + 1}. **Image**: ${escapeMarkdown(image)} \n\n`

    const findingsForImage = allImagesFindings.filter(
      (finding) =>
        getImageNameWithTag(finding.resource.name, finding.resource.tag) ===
        image
    )

    findingsForImage.forEach((finding) => {
      if ("introducedThrough" in finding) {
        const introducedThrough =
          finding.introducedThrough
            .filter((item) => item.baseImageDetails?.baseImageDigest !== "")
            .pop() || null

        if (introducedThrough?.baseImageDetails?.baseImageNames?.length) {
          const baseImageNames =
            introducedThrough.baseImageDetails.baseImageNames
          const baseImageString = baseImageNames[0]
          const isThirdParty = thirdPartyFindingsSet.has(
            finding as ThirdPartyFinding
          )
          const label = isThirdParty ? "From Third party" : "From Base image"
          acc += `    * ${label}: \`\`\`${baseImageString}\`\`\`\n`
        }
      }
    })

    acc += "\n"
  })

  return "\n\n###Impacted Images\n\n" + acc
}

const getFixHTML = (fix: Fix, attr: Attr): string => {
  const { linkToDrawer, t } = attr
  //currently we only support in one issue per fix:
  const issue = fix.issues[0]

  if (IssueDomain.isCodeIssue(issue)) {
    return getVulnerableCodeFix(fix, attr)
  }

  const tKey = "inventory.images.drawer.issue."

  const issueInfo = getIssueInfo(issue, linkToDrawer)
  const packageUpdates = getPackageUpdates(fix, issue.issueType, t)
  const impactedImages = getImagesList(fix)
  if (issue.issueType === "Malicious_Package") {
    return getMaliciousPackageFix(issueInfo, packageUpdates, impactedImages)
  }

  const fixVersions = getFixVersions(issue)

  const { fixInsights } = issue
  if (!fixInsights.hasFixVersion && !fixInsights.hasKodemRemediation) {
    return getUnfixable(issueInfo, fixVersions, impactedImages, t, tKey)
  }

  const baseImageFixes = getBaseImageFixes(
    fix.baseImageFindings,
    fixInsights.hasFixVersion,
    t,
    tKey
  )
  const thirdPartyFixes = getThirdPartyFixes(
    fix.thirdPartyFindings || [],
    fixInsights.hasFixVersion,
    t,
    tKey
  )
  return `${issueInfo}\n\n
  \n\n###How To Fix\n\n
  ${packageUpdates}\n
  ${baseImageFixes}\n
  ${thirdPartyFixes}\n
  \n\n${fixVersions}\n\n
  ${impactedImages}\n\n`
}

const getMaliciousPackageFix = (
  issueInfo: string,
  packageUpdates: string,
  impactedImages: string
): string => {
  return `${issueInfo}\n\n
  \n\n###How To Fix\n\n
  ${packageUpdates}\n
  ${impactedImages}\n\n`
}

const getVulnerableCodeFix = (fix: Fix, attr: Attr): string => {
  const { t, linkToDrawer } = attr
  const issue = fix.issues[0]
  const issueInfo = getIssueInfo(issue, linkToDrawer)
  // vulnurable code fix can includes only one code repo finding for one issue
  const vulnFinding = fix.vulnerabilityCodeFindings[0] as
    VulnerableCodeFinding | ExposedSecretFinding
  const vulnDescription = vulnFinding.description
  const fixDescription = vulnFinding?.remedies?.[0].fix
    ? vulnFinding?.remedies?.[0].fix
    : t("issueTypes.components.fixLocationCard.noFixesDescription")
  const fixLocations = vulnFinding?.introducedThrough?.map(
    ({ url, displayText }) => `* [${escapeMarkdown(displayText)}](${url})`
  )

  return `${issueInfo}\n\n
  \n\n###Description\n\n
  \n\n${vulnDescription}\n\n
  \n\n###Where To Fix\n\n
  \n\n${fixLocations.join("\n")}\n\n
  \n\n###What To Do\n\n
  \n\n${fixDescription}\n\n`
}

const getUnfixable = (
  issueInfo: string,
  fixVersions: string,
  impactedImages: string,
  t: TFunction,
  tKey: string
): string => {
  const unfixableMessage = t(`${tKey}kodemUnfixable`)

  return `${issueInfo}\n\n
  \n\n###What to do\n\n
  \n\n${fixVersions}\n\n
  ${unfixableMessage}\n
  ${impactedImages}\n\n`
}

export const getIssueTitle = (issue: Issue) => {
  const { riskId, filePath } = issue

  if (IssueDomain.isCodeIssue(issue))
    return escapeMarkdown(`${riskId} on ${filePath || ""}`)

  const { cwe, packageName } = issue

  const issueName = cwe?.description ? `${cwe.description}: ${riskId}` : riskId

  return escapeMarkdown(`${issueName} on ${packageName}`)
}
